import { ChartArtifact_ } from "@redotech/ai-core-agent-artifact-schema/artifact-schema";
import { unaryStreamRpc } from "@redotech/rpc/definition";
import * as z from "zod";

const MessageSchema_ = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string(),
});

const FileContentSchema_ = z.object({
  fileName: z.string(),
  headers: z.array(z.string()).optional(),
  sampleRows: z.array(z.record(z.string(), z.string())).optional(),
  totalRowCount: z.number().optional(),
  mediaType: z.string().optional(),
});

const TextDeltaEvent_ = z.object({
  type: z.literal("text-delta"),
  textDelta: z.string(),
});

const ToolCallEvent_ = z.object({
  type: z.literal("tool-call"),
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.unknown(),
});

const StatusEvent_ = z.object({
  type: z.literal("status"),
  status: z.string(),
});

const IterationCompleteEvent_ = z.object({
  type: z.literal("iteration-complete"),
  pendingToolsCount: z.number(),
});

const ThreadIdEvent_ = z.object({
  type: z.literal("thread-id"),
  threadId: z.string(),
});

const CompleteEvent_ = z.object({
  type: z.literal("complete"),
  threadId: z.string(),
});

const OperationCompleteEvent_ = z.object({
  type: z.literal("operation-complete"),
  operationType: z.enum([
    "create",
    "update",
    "delete",
    "link_suppliers",
    "create_supplier",
    "update_supplier",
    "create_bom",
    "update_bom",
    "create_location",
    "set_product_locations",
    "create_purchase_order",
    "update_purchase_order",
    "adjust_inventory",
    "adjust_forecasts",
    "create_transfer",
    "submit_transfer",
  ]),
  productCount: z.number().optional(),
  skuCount: z.number().optional(),
  supplierCount: z.number().optional(),
  bomCount: z.number().optional(),
  locationCount: z.number().optional(),
  purchaseOrderCount: z.number().optional(),
  transferCount: z.number().optional(),
  forecastAdjustmentCount: z.number().optional(),
  linksUpdated: z.number().optional(),
  fields: z.array(z.string()).optional(),
  forecastingEnabled: z.boolean().optional(),
  toolCallId: z.string().optional(),
  adjustmentSessionKind: z.string().optional(),
  failed: z.boolean().optional(),
});

const FileCreatedEvent_ = z.object({
  type: z.literal("file-created"),
  fileName: z.string(),
  s3Key: z.string(),
  toolCallId: z.string().optional(),
});

const TableCreatedEvent_ = z.object({
  type: z.literal("table-created"),
  title: z.string().optional(),
  headers: z.array(z.string()),
  rows: z.array(z.array(z.string())),
  toolCallId: z.string().optional(),
});

const ChartCreatedEvent_ = z.object({
  type: z.literal("chart-created"),
  chart: ChartArtifact_,
  toolCallId: z.string().optional(),
});

const ErrorEvent_ = z.object({ type: z.literal("error"), message: z.string() });

export const IntelligentInventoryStreamEventSchema = z.discriminatedUnion(
  "type",
  [
    TextDeltaEvent_,
    ToolCallEvent_,
    StatusEvent_,
    IterationCompleteEvent_,
    OperationCompleteEvent_,
    FileCreatedEvent_,
    TableCreatedEvent_,
    ChartCreatedEvent_,
    ThreadIdEvent_,
    CompleteEvent_,
    ErrorEvent_,
  ],
);
export type IntelligentInventoryStreamEvent = z.infer<
  typeof IntelligentInventoryStreamEventSchema
>;

export const streamIntelligentInventory = unaryStreamRpc({
  name: "streamIntelligentInventory" as const,
  input: z.object({
    threadId: z.string().optional(),
    messages: z.array(MessageSchema_),
    fileContents: z.array(FileContentSchema_).optional(),
  }),
  output: IntelligentInventoryStreamEventSchema,
});
